/**
 * ShellSwitcher — cross between the office and field shells without logging
 * out and back in.
 *
 * Shown only to users `useFieldAccess()` entitles: everyone on a Lite tenant
 * (1–3 people, no office/field split) and individually-flagged dual-access
 * users on a Pro tenant.
 *
 * Crossing to the field also calls `setAuthExpiry()` — TechAuthGuard enforces
 * a 90-day re-auth window, and a user who signed in through the office has no
 * `tech_auth_expiry` set, so without this they'd land on the PIN screen
 * immediately. They authenticated minutes ago as themselves; the clock starts
 * from that, not from a second PIN entry.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, HardHat, Loader2 } from 'lucide-react';
import { enterFieldSurface, enterOfficeSurface, getSession } from '@/lib/session';
import { setAuthExpiry } from '@/hooks/useTechAuth';
import { useFieldAccess } from '@/hooks/useFieldAccess';
import { useTerminology } from '@/hooks/useTradePack';

export type Shell = 'office' | 'field';

interface Props {
  /** Which shell the switcher is rendered in — it offers the other one. */
  current: Shell;
  /** Compact icon-only rendering for tight navs. */
  compact?: boolean;
  className?: string;
}

export function ShellSwitcher({ current, compact = false, className = '' }: Props) {
  const navigate = useNavigate();
  const { canUseBothShells, reason } = useFieldAccess();
  const { term } = useTerminology();
  const [busy, setBusy] = useState(false);

  // A Lite tenant has ONE shell (/app) — there is nothing to switch TO, and
  // offering the switch was exactly what made the blended approach confusing.
  // The switcher now only serves individually-flagged dual-access users on a
  // Pro tenant, which is the case it was built for.
  if (!canUseBothShells || reason === 'lite-tenant') return null;

  const goingToField = current === 'office';
  // 'Field' rather than the tech term, because the destination is a place,
  // not a person — but a pack that renames the role usually renames the
  // place too, so honour the override when there is one.
  const label = goingToField ? term('fieldShell', 'Field') : term('officeShell', 'Office');
  const Icon = goingToField ? HardHat : Building2;

  const cross = () => {
    if (busy) return;
    setBusy(true);
    if (goingToField) {
      const { userId, userName } = getSession();
      enterFieldSurface();
      setAuthExpiry();
      navigate('/tech', { state: { techId: userId ?? '', techName: userName ?? '' } });
    } else {
      enterOfficeSurface();
      navigate('/home');
    }
    // No setBusy(false) — we've navigated away and this tree unmounts.
  };

  return (
    <button
      type="button"
      onClick={cross}
      disabled={busy}
      title={`Switch to ${label}`}
      aria-label={`Switch to ${label}`}
      className={
        className ||
        `flex items-center gap-2 ${compact ? 'px-2.5' : 'px-3'} py-2 rounded-lg text-sm font-semibold ` +
          'text-theme-secondary hover:text-theme-primary hover:bg-surface-hover ' +
          'border border-theme transition-colors disabled:opacity-50'
      }
    >
      {busy ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Icon className="w-4 h-4" />
      )}
      {!compact && <span>{label}</span>}
    </button>
  );
}
